import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import ContractsWidget from "@/components/contracts-widget";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  LogOut,
  DollarSign,
  Users,
  Percent,
  CheckCircle,
  Copy,
  Link as LinkIcon,
  Sparkles,
} from "lucide-react";

function formatDate(d: string | Date) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatMoney(amount: string | number | null | undefined): string {
  const num = Number(amount || 0);
  return `$${num.toFixed(2)}`;
}

export default function AffiliateDashboard() {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const { data: profile, isLoading: loadingProfile } = useQuery<any>({
    queryKey: ["/api/affiliate/profile"],
  });

  const { data: commissions = [], isLoading: loadingCommissions } = useQuery<any[]>({
    queryKey: ["/api/affiliate/commissions"],
  });

  const { data: referrals = [], isLoading: loadingReferrals } = useQuery<any[]>({
    queryKey: ["/api/affiliate/referrals"],
  });

  const referralLink = profile?.referralCode
    ? `${window.location.origin}/marketplace?ref=${profile.referralCode}`
    : "";

  const handleCopyLink = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast({ title: "Copied", description: "Referral link copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Failed to copy", variant: "destructive" });
    }
  };

  const statusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case "paid":
        return "default";
      case "pending":
        return "secondary";
      case "cancelled":
        return "destructive";
      default:
        return "outline";
    }
  };

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-muted/20 flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-muted/20">
      <header
        className="bg-card border-b px-4 flex items-center justify-between gap-2 sticky top-0 z-50"
        style={{
          height: "calc(3.5rem + env(safe-area-inset-top, 0px))",
          paddingTop: "env(safe-area-inset-top, 0px)",
        }}
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h1
              className="font-display font-bold text-lg leading-tight truncate"
              data-testid="text-affiliate-title"
            >
              Staio - Affiliate Portal
            </h1>
            <p className="text-xs text-muted-foreground truncate">
              {(user as any)?.fullName || (user as any)?.username}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => logout()}
          data-testid="button-affiliate-logout"
        >
          <LogOut className="h-5 w-5" />
        </Button>
      </header>

      <div
        className="p-4 space-y-6 max-w-4xl mx-auto"
        style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="text-total-earnings">
                  {formatMoney(profile?.totalEarnings)}
                </p>
                <p className="text-xs text-muted-foreground">Total Earnings</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="text-total-referrals">
                  {profile?.totalReferrals ?? 0}
                </p>
                <p className="text-xs text-muted-foreground">Total Referrals</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Percent className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="text-commission-rate">
                  {profile?.commissionPct ?? 0}%
                </p>
                <p className="text-xs text-muted-foreground">Commission Rate</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-sm font-semibold" data-testid="text-affiliate-status">
                  {profile?.status === "active" ? "Active" : profile?.status || "N/A"}
                </p>
                <p className="text-xs text-muted-foreground">Status</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-base font-display flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              Your Referral Link
            </CardTitle>
          </CardHeader>
          <CardContent>
            {referralLink ? (
              <div className="flex items-center gap-2 flex-wrap">
                <code
                  className="flex-1 min-w-0 text-sm bg-muted px-3 py-2 rounded-md truncate"
                  data-testid="text-referral-link"
                >
                  {referralLink}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                  data-testid="button-copy-referral-link"
                >
                  {copied ? (
                    <CheckCircle className="w-4 h-4 mr-1" />
                  ) : (
                    <Copy className="w-4 h-4 mr-1" />
                  )}
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="text-no-referral-link">
                No referral code assigned yet
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-display">Recent Commissions</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingCommissions ? (
              <div className="p-4 space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (commissions as any[]).length === 0 ? (
              <div className="p-8 text-center text-muted-foreground" data-testid="text-no-commissions">
                No commissions yet
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Guest</TableHead>
                    <TableHead>Check-In</TableHead>
                    <TableHead>Check-Out</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(commissions as any[]).map((c: any) => (
                    <TableRow key={c.id} data-testid={`row-commission-${c.id}`}>
                      <TableCell className="font-medium" data-testid={`text-commission-guest-${c.id}`}>
                        {c.guestName || "N/A"}
                      </TableCell>
                      <TableCell data-testid={`text-commission-checkin-${c.id}`}>
                        {c.checkIn ? formatDate(c.checkIn) : "—"}
                      </TableCell>
                      <TableCell data-testid={`text-commission-checkout-${c.id}`}>
                        {c.checkOut ? formatDate(c.checkOut) : "—"}
                      </TableCell>
                      <TableCell data-testid={`text-commission-source-${c.id}`}>
                        {c.source || "—"}
                      </TableCell>
                      <TableCell className="text-right font-medium" data-testid={`text-commission-amount-${c.id}`}>
                        {formatMoney(c.amount)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(c.status)} data-testid={`badge-commission-status-${c.id}`}>
                          {c.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-display">Referred Bookings</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingReferrals ? (
              <div className="p-4 space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : (referrals as any[]).length === 0 ? (
              <div className="p-8 text-center text-muted-foreground" data-testid="text-no-referrals">
                No referred bookings yet
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Guest Name</TableHead>
                    <TableHead>Check-In</TableHead>
                    <TableHead>Check-Out</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(referrals as any[]).map((r: any) => (
                    <TableRow key={r.id} data-testid={`row-referral-${r.id}`}>
                      <TableCell className="font-medium" data-testid={`text-referral-guest-${r.id}`}>
                        {r.guestName || "N/A"}
                      </TableCell>
                      <TableCell data-testid={`text-referral-checkin-${r.id}`}>
                        {r.checkInDate ? formatDate(r.checkInDate) : "—"}
                      </TableCell>
                      <TableCell data-testid={`text-referral-checkout-${r.id}`}>
                        {r.checkOutDate ? formatDate(r.checkOutDate) : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={r.status === "confirmed" ? "default" : r.status === "cancelled" ? "destructive" : "secondary"}
                          data-testid={`badge-referral-status-${r.id}`}
                        >
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium" data-testid={`text-referral-total-${r.id}`}>
                        {formatMoney(r.totalPrice)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <ContractsWidget />
      </div>
    </div>
  );
}
